import { Injectable } from '@jack-henry/frontend-utils/di';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import { CompanyUserSummary } from '@treasury/domain/company/user/entities';
import { exists } from '@treasury/utils';
import { BoCompanyUserActionHelper, ConsumerCompanyUserActionHelper } from './helpers';
import { CompanyUserActionType } from './user-actions.types';

/**
 * Company user action service for BOTH the BackOffice and Consumer apps.
 */
@Injectable()
export class CompanyUserActionService {
    constructor(
        private readonly config: ConfigurationService,
        private readonly consumerUserActionHelper: ConsumerCompanyUserActionHelper,
        private readonly boCompanyUserActionHelper: BoCompanyUserActionHelper
    ) {}

    public get userActions() {
        return this.userActionDomainHelper().userActions;
    }

    public async loadCompanyUserActions(company?: CompanyViewModel): Promise<void> {
        if (this.isBoApp && !exists(company)) {
            throw new Error('Company is required to load BackOffice user actions');
        }
        return this.isBoApp && !!company
            ? this.boCompanyUserActionHelper.loadCompanyUserActions(company)
            : this.consumerUserActionHelper.loadCompanyUserActions();
    }

    /**
     * Handles the click event for a user action.
     *
     * @param userAction - The action to be performed, identified by its ID.
     * @param user - The user on whom the action is to be performed.
     */
    public handleActionClick(userAction: CompanyUserActionType, user: CompanyUserSummary) {
        const currentAction = this.userActions.find(ua => ua.type === userAction);
        currentAction?.action(user);
    }

    // Is the service being used in Backoffice app
    private get isBoApp() {
        return this.config.app === AppType.BackOffice;
    }

    private userActionDomainHelper() {
        return this.isBoApp ? this.boCompanyUserActionHelper : this.consumerUserActionHelper;
    }
}
